import axios from 'axios';

import { API } from '../common';

const TOKEN_KEY = 'contractor_access_token';
const SESSION_KEY = 'contractor_session';
const USERNAME_KEY = 'contractor_username';

export type LoginStartResponseType = {
    email?: string;
    phone?: string;
    session: string;
};

export type LoginResponseType = {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
};

export class AuthService {
    private static instance: AuthService;

    private constructor() {}

    public static getInstance(): AuthService {
        if (!AuthService.instance) {
            AuthService.instance = new AuthService();
        }
        return AuthService.instance;
    }

    /**
     * Check if valid token exists
     */
    public hasValidToken(): boolean {
        const token = localStorage.getItem(TOKEN_KEY);
        return !!token;
    }

    /**
     * Get access token
     */
    public getToken(): string | null {
        return localStorage.getItem(TOKEN_KEY);
    }

    /**
     * Check if token is expired
     */
    public isTokenExpired(): boolean {
        const token = this.getToken();
        if (!token) {
            return true;
        }

        try {
            // Parse JWT token to get expiration time
            const payload = JSON.parse(atob(token.split('.')[1]));
            const exp = payload.exp;

            if (!exp) {
                return false; // If no expiration, assume valid
            }

            // Check if token expires in less than 60 seconds
            const now = Math.floor(Date.now() / 1000);
            return exp - now < 60;
        } catch (error) {
            console.error('Failed to parse token:', error);
            return false; // If can't parse, assume valid and let server decide
        }
    }

    /**
     * Initiate login process
     */
    public async loginStart(uuid: string): Promise<LoginStartResponseType> {
        try {
            const response = await axios.post<LoginStartResponseType>(`${API.RAPI_URL}/login-start`, {
                username: uuid,
            });

            // Save session and username for subsequent use
            localStorage.setItem(SESSION_KEY, response.data.session);
            localStorage.setItem(USERNAME_KEY, uuid);

            return response.data;
        } catch (error) {
            console.error('Login start failed:', error);
            throw error;
        }
    }

    /**
     * Complete login with OTP code
     */
    public async loginComplete(otp: string): Promise<void> {
        const username = localStorage.getItem(USERNAME_KEY);
        const session = localStorage.getItem(SESSION_KEY);

        if (!username || !session) {
            throw new Error('No active login session');
        }

        try {
            const response = await axios.post<LoginResponseType>(
                `${API.RAPI_URL}/login`,
                {
                    username,
                    session,
                    otp,
                },
                { withCredentials: true },
            );

            // Save access token
            localStorage.setItem(TOKEN_KEY, response.data.accessToken);

            // Clear temporary data
            localStorage.removeItem(SESSION_KEY);
        } catch (error) {
            console.error('Login complete failed:', error);
            throw error;
        }
    }

    /**
     * Refresh access token
     */
    public async refreshToken(): Promise<string> {
        try {
            const response = await axios.get<LoginResponseType>(`${API.RAPI_URL}/refresh-token`, {
                withCredentials: true, // Refresh token is sent automatically in cookies
            });

            // Save new access token
            localStorage.setItem(TOKEN_KEY, response.data.accessToken);

            return response.data.accessToken;
        } catch (error) {
            console.error('Token refresh failed:', error);
            this.logout();
            throw error;
        }
    }

    /**
     * Logout user
     */
    public logout(): void {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(SESSION_KEY);
        localStorage.removeItem(USERNAME_KEY);
    }

    /**
     * Get stored contact (email or phone) from session
     */
    public getStoredContact(): string | null {
        return localStorage.getItem(SESSION_KEY);
    }
}

export const authService = AuthService.getInstance();
